import React from "react";
import "./App.css";
import { TableauIndex, FoundationIndex } from "./types";
import useGameState from "./hooks/useGameState";
import CurrentCard from "./components/CurrentCard";
import FoundationPile from "./components/FoundationPile";
import TableauPile from "./components/TableauPile";
import GameStatus from "./components/GameStatus";
import Button from "./components/Button";
import { RefreshIcon, UndoIcon } from "./components/icons";

function App(): React.ReactElement {
  const {
    gameState,
    newGame,
    selectCard,
    selectDestination,
    getValidFoundationDestinations,
    undo,
    canUndo,
  } = useGameState();

  const {
    stock,
    currentCard,
    foundations,
    tableau,
    gameWon,
    gameOver,
    selectedCard,
    selectedSource,
  } = gameState;

  const handleTableauClick = (index: TableauIndex) => {
    // If drawn card is selected, move it to this tableau pile
    if (selectedCard && selectedSource === "current") {
      selectDestination("tableau", index);
    }
    // If tableau has cards
    else if (tableau[index].length > 0) {
      // If this pile's card is already selected, this will deselect it
      const topCard = tableau[index][tableau[index].length - 1];
      selectCard(topCard, index);
    }
    // If tableau is empty and we have a current card but nothing selected
    else if (currentCard && !selectedCard) {
      selectCard(currentCard, "current");
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-slate-800 text-white p-2 pt-[25vh]">
      <div className="flex flex-col items-center">
        {/* Game status at the top */}
        <div className="w-full flex justify-center items-center mb-4">
          <GameStatus gameWon={gameWon} gameOver={gameOver} />
        </div>

        {/* Card count above */}
        <div className="flex flex-col items-center mb-6">
          {/* Display card count text */}
          <div className="text-sm text-gray-300 mb-3">
            {stock.length > 0 ? (
              <span>{stock.length} cards left</span>
            ) : (
              <span>No cards left</span>
            )}
          </div>

          {/* Buttons and draw deck in the same row */}
          <div className="flex items-center justify-center">
            {/* New Game Button */}
            <div className="mr-12">
              <Button
                onClick={newGame}
                variant="icon"
                title="New Game"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <RefreshIcon className="w-5 h-5" />
              </Button>
            </div>

            {/* Draw deck in the middle */}
            <CurrentCard
              card={currentCard}
              isSelected={selectedCard !== null && selectedSource === "current"}
              onClick={() => currentCard && selectCard(currentCard, "current")}
            />

            {/* Undo Button */}
            <div className="ml-12">
              <Button
                onClick={undo}
                variant="icon"
                className={
                  !canUndo
                    ? "opacity-40 cursor-not-allowed bg-gray-600"
                    : "bg-amber-600 hover:bg-amber-700"
                }
                disabled={!canUndo}
                title="Undo Last Move"
              >
                <UndoIcon className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Game board */}
        <div className="w-full max-w-sm mx-auto">
          {/* Foundation piles */}
          <div className="flex justify-center mb-3 gap-3">
            {foundations.map((foundation, index) => (
              <FoundationPile
                key={`foundation-${index}`}
                cards={foundation}
                isValidDestination={
                  selectedCard !== null &&
                  getValidFoundationDestinations(selectedCard).includes(
                    index as FoundationIndex,
                  )
                }
                onClick={() => selectDestination("foundation", index)}
              />
            ))}
          </div>

          {/* Tableau (waste piles) */}
          <div className="flex justify-center gap-3">
            {tableau.map((pile, index) => {
              const isValidDestination =
                selectedCard !== null && selectedSource === "current";
              const isTopCardSelected =
                pile.length > 0 &&
                selectedCard !== null &&
                selectedSource === index;

              return (
                <TableauPile
                  key={`tableau-${index}`}
                  cards={pile}
                  isSelected={isTopCardSelected}
                  isValidDestination={isValidDestination}
                  onClick={() => handleTableauClick(index as TableauIndex)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
